import { xdsVars } from '@soteria/design-system-ve';

import * as styles from './ProgressBar.css';

export interface ProgressBarProps {
  /**
   * Whether the progress bar should animate
   */
  animated?: boolean;
  /**
   * Accessible label for the progress bar
   */
  'aria-label'?: string;
  /**
   * Color variant of the progress bar
   */
  color?: 'primary' | 'success' | 'warning' | 'error' | 'secondary' | 'blue' | 'purple';
  /**
   * Size variant of the progress bar
   */
  size?: 'small' | 'medium' | 'large' | 'compact' | 'panel';
  /**
   * Text to display alongside the progress bar
   */
  text?: string;
  /**
   * Alignment of the text
   */
  textAlign?: 'left' | 'center' | 'right';
  /**
   * Position of the text relative to the progress bar
   */
  textPosition?: 'before' | 'after' | 'overlay';
  /**
   * Progress value (0-100)
   */
  value: number;
}

// Color mapping for progress bar variants
const COLOR_STYLES = {
  success: { backgroundColor: xdsVars.colors.activityOnline },
  warning: { backgroundColor: xdsVars.colors.activityWarning },
  error: { backgroundColor: xdsVars.colors.activityError },
  secondary: { backgroundColor: xdsVars.colors.contentSecondary },
  primary: { backgroundColor: xdsVars.colors.productDataVizXbox },
  blue: { backgroundColor: xdsVars.colors.productDataVizWindows },
  purple: { backgroundColor: xdsVars.colors.productDataVizChevron },
} as const;

// Size class mapping
const SIZE_CLASSES = {
  small: styles.small,
  medium: styles.medium,
  large: styles.large,
  compact: styles.compact,
  panel: styles.panel,
} as const;

// Text alignment class mapping
const TEXT_ALIGN_CLASSES = {
  left: styles.textAlignLeft,
  center: styles.textAlignCenter,
  right: styles.textAlignRight,
} as const;

// Text position class mapping
const TEXT_POSITION_CLASSES = {
  before: styles.textBefore,
  after: styles.textAfter,
  overlay: styles.textAfter, // fallback for overlay
} as const;

/**
 * Get color style for progress bar
 */
function getColorStyle(color: ProgressBarProps['color'] = 'primary') {
  return COLOR_STYLES[color] || COLOR_STYLES.primary;
}

/**
 * Get size class for progress bar
 */
function getSizeClass(size: ProgressBarProps['size'] = 'medium') {
  return SIZE_CLASSES[size] || SIZE_CLASSES.medium;
}

/**
 * Get text alignment class
 */
function getTextAlignClass(textAlign: ProgressBarProps['textAlign'] = 'left') {
  return TEXT_ALIGN_CLASSES[textAlign] || TEXT_ALIGN_CLASSES.left;
}

/**
 * Get text position class
 */
function getTextPositionClass(textPosition: ProgressBarProps['textPosition'] = 'after') {
  if (textPosition === 'overlay') return TEXT_POSITION_CLASSES.after;

  return TEXT_POSITION_CLASSES[textPosition] || TEXT_POSITION_CLASSES.after;
}

/**
 * Progress bar configuration
 */
interface ProgressBarConfig {
  progressPercentage: number;
  sizeClass: string;
  animated: boolean;
  ariaLabel?: string;
  color?: ProgressBarProps['color'];
}

/**
 * Create progress bar element
 */
function createProgressBar(config: ProgressBarConfig) {
  const { progressPercentage, sizeClass, animated, ariaLabel, color } = config;

  return (
    <div className={`${styles.progressBarContainer} ${sizeClass}`}>
      <div
        aria-label={ariaLabel}
        aria-valuemax={100}
        aria-valuemin={0}
        aria-valuenow={progressPercentage}
        className={`${styles.progressBar} ${animated ? styles.animated : ''}`}
        role="progressbar"
      >
        <div
          className={styles.progressBarFill}
          style={{
            width: `${progressPercentage}%`,
            ...getColorStyle(color),
          }}
        />
      </div>
    </div>
  );
}

/**
 * Generic progress bar component for displaying progress indicators
 * Supports multiple sizes, colors, and text positioning options
 */
export function ProgressBar({
  animated = false,
  'aria-label': ariaLabel,
  color = 'primary',
  size = 'medium',
  text,
  textAlign = 'left',
  textPosition = 'after',
  value,
}: ProgressBarProps) {
  const progressPercentage = Math.min(Math.max(value, 0), 100);
  const sizeClass = getSizeClass(size);
  const progressBar = createProgressBar({
    progressPercentage,
    sizeClass,
    animated,
    ariaLabel,
    color,
  });

  if (!text) {
    return progressBar;
  }

  const textElement = <span className={`${styles.progressText} ${getTextAlignClass(textAlign)}`}>{text}</span>;

  if (textPosition === 'overlay') {
    return (
      <div className={styles.progressWithOverlayText}>
        {progressBar}
        {textElement}
      </div>
    );
  }

  return (
    <div className={`${styles.progressWithText} ${getTextPositionClass(textPosition)}`}>
      {textPosition === 'before' && textElement}
      {progressBar}
      {textPosition === 'after' && textElement}
    </div>
  );
}
